import { useState } from 'react'
import { Clock, ChevronDown, ChevronUp } from 'lucide-react'
import type { VenueDetailOperatingHoursComponent } from '../../../types/sdui-arena-detail'

interface VenueDetailOperatingHoursProps {
  config: VenueDetailOperatingHoursComponent
}

export function VenueDetailOperatingHours({ config }: VenueDetailOperatingHoursProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const { hours } = config

  if (!hours || hours.length === 0) {
    return null
  }

  // Get current day (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
  const today = new Date().getDay()
  const todayHours = hours.find(h => h.dayOfWeek === today)

  const getTodayStatus = () => {
    if (!todayHours) return { text: 'Horário não disponível', isOpen: false }

    if (todayHours.isClosed) {
      return { text: 'Fechado hoje', isOpen: false }
    }

    if (todayHours.isOpen24h) {
      return { text: 'Aberto 24 horas', isOpen: true }
    }

    if (todayHours.formattedHours) {
      return {
        text: todayHours.formattedHours,
        isOpen: true
      }
    }

    return { text: 'Aberto', isOpen: true }
  }

  const status = getTodayStatus()

  const getHoursDisplay = (day: typeof hours[0]) => {
    if (day.isClosed) {
      return 'Fechado'
    }

    if (day.isOpen24h) {
      return 'Aberto 24 horas'
    }

    if (day.formattedHours) {
      return day.formattedHours
    }

    return 'Aberto'
  }

  return (
    <section>
      <h2 className="text-xl font-bold text-text-primary mb-3">Horários de funcionamento</h2>
      <div className="border border-border-light rounded-lg overflow-hidden">
        {/* Today's hours - always visible */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between p-4 hover:bg-background-secondary transition-colors"
        >
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-brand-primary" />
            <div className="text-left">
              <div className="font-medium text-text-primary">
                {todayHours?.dayLabel || 'Hoje'}
              </div>
              <div className={`text-sm ${status.isOpen ? 'text-green-600 font-medium' : 'text-text-secondary'}`}>
                {status.text}
              </div>
            </div>
          </div>
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-text-secondary" />
          ) : (
            <ChevronDown className="w-5 h-5 text-text-secondary" />
          )}
        </button>

        {/* All week hours - expandable */}
        {isExpanded && (
          <div className="border-t border-border-light">
            {hours.map((day) => {
              const isToday = day.dayOfWeek === today

              return (
                <div
                  key={day.dayOfWeek}
                  className={`flex items-center justify-between p-4 ${
                    isToday ? 'bg-brand-primary/5' : ''
                  } ${day.dayOfWeek !== hours[hours.length - 1].dayOfWeek ? 'border-b border-border-light' : ''}`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`font-medium ${isToday ? 'text-brand-primary' : 'text-text-primary'}`}>
                      {day.dayLabel}
                    </span>
                    {isToday && (
                      <span className="text-xs px-2 py-0.5 bg-brand-primary text-white rounded-full">
                        Hoje
                      </span>
                    )}
                  </div>
                  <div className={`text-sm text-right ${day.isClosed ? 'text-text-secondary' : 'text-text-primary'}`}>
                    {getHoursDisplay(day)}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </section>
  )
}
